import { gql } from '@apollo/client';
import { Button, Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { useAuth } from '~auth/useAuth';
import {
  CopyIcon,
  EditIcon,
  LoadingIcon,
  MoreIcon,
  NoDisplayIcon,
  SyncIcon,
  TrashIcon,
} from '~components/ui/icons';
import { ScheduleDetailMoreActions_ScheduleFragment } from './__generated__/ScheduleDetailMoreActions.graphql';

type Props = {
  schedule: ScheduleDetailMoreActions_ScheduleFragment;
  isSchedulesLimitReached?: boolean;
  onRemoveDisplays: () => void;
  onSync: () => void;
  onDelete: () => void;
  onRename: () => void;
  onDuplicate: () => void;
};

export function ScheduleDetailMoreActions({
  schedule,
  isSchedulesLimitReached = false,
  onRemoveDisplays,
  onSync,
  onDelete,
  onRename,
  onDuplicate,
}: Props) {
  const { verifyUserPermissions } = useAuth();

  const hasScheduleCreatePermission = verifyUserPermissions([Permission.PowerscheduleCreate]);
  const hasScheduleUpdatePermission = verifyUserPermissions([Permission.PowerscheduleUpdate]);
  const hasScheduleDeletePermission = verifyUserPermissions([Permission.PowerscheduleDelete]);
  const hasDisplayContentUpdatePermission = verifyUserPermissions([
    Permission.DisplayContentUpdate,
  ]);

  const hasDisplays = schedule.allDisplays.length > 0;
  const hasOutOfSyncDisplays = schedule.outOfSyncDisplays.length > 0;
  const isSyncing = schedule.syncingDisplays.length > 0;
  const isRemoving = schedule.removingDisplays.length > 0;
  const isUpdating = isSyncing || isRemoving;

  return (
    <Menu>
      <MenuButton as={Button} aria-label="Options" variant="outline">
        <MoreIcon />
      </MenuButton>
      <MenuList>
        <MenuItem
          icon={<EditIcon />}
          onClick={onRename}
          isDisabled={!hasScheduleUpdatePermission || isSchedulesLimitReached}
        >
          {t('rename')}
        </MenuItem>

        <MenuItem
          icon={<CopyIcon />}
          isDisabled={!hasScheduleCreatePermission || isSchedulesLimitReached || isUpdating}
          onClick={onDuplicate}
        >
          {t('duplicate')}
        </MenuItem>

        <MenuItem
          icon={isSyncing ? <LoadingIcon /> : <SyncIcon />}
          isDisabled={!hasDisplayContentUpdatePermission || isUpdating || !hasOutOfSyncDisplays}
          onClick={onSync}
        >
          {t('sync')}
        </MenuItem>
        <MenuItem
          icon={isRemoving ? <LoadingIcon /> : <NoDisplayIcon />}
          isDisabled={!hasDisplayContentUpdatePermission || isUpdating || !hasDisplays}
          onClick={onRemoveDisplays}
        >
          {t('removeFromDisplays')}
        </MenuItem>
        <MenuItem
          icon={<TrashIcon color="red.500" />}
          color={'red.500'}
          isDisabled={!hasScheduleDeletePermission || isUpdating}
          onClick={onDelete}
        >
          {t('delete')}
        </MenuItem>
      </MenuList>
    </Menu>
  );
}

ScheduleDetailMoreActions.graphql = {
  fragments: {
    ScheduleDetailMoreActions_schedule: gql`
      fragment ScheduleDetailMoreActions_schedule on Schedule {
        id
        allDisplays: displays {
          id
        }
        outOfSyncDisplays: displays(filter: { state: OUT_OF_SYNC }) {
          id
        }
        syncingDisplays: displays(filter: { state: SYNCING }) {
          id
        }
        removingDisplays: displays(filter: { state: REMOVING }) {
          id
        }
      }
    `,
  },
};
